/**
 * Demos pédagogiques interactives — Code 2 Roues (version MOTO)
 * Adapté depuis codevoiture.fr avec autorisations · 02/10/2026
 * 8 démos : Mariotte, Angles morts, Effet tunnel, Temps de réaction,
 *          Énergie cinétique, Panneaux flash, PVT, Vision sous alcool
 *
 * 8 démos calibrées par des moniteurs ECSR pour faire comprendre
 * en 30 secondes ce qu'un cours magistral met 30 minutes à expliquer.
 *
 * Usage hub  : <DemoCard slug="mariotte" />
 * Usage embed: <DemoEmbed slug="mariotte" />  (dans un article)
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Eye, Car, Gauge, Timer, Zap, Play, RotateCcw, Info, Octagon, Coffee, Wine, Compass, Share2, Check, Trophy, AlertTriangle, Route } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────
// 1. TACHE AVEUGLE DE MARIOTTE (eye blind spot)
// ─────────────────────────────────────────────────────────────────
export function MariotteDemo() {
  const [side, setSide] = useState('right'); // which eye to close
  const isRight = side === 'right';
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7" data-testid="demo-mariotte">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-red-600/15 border border-red-700/30 flex items-center justify-center text-red-400 shrink-0"><Eye size={18} /></div>
        <div>
          <h3 className="font-serif-editorial text-white text-[20px] sm:text-[26px] leading-tight">Découvre ta tache aveugle</h3>
          <p className="text-white/60 text-[13px] sm:text-[14px] mt-1">Chaque œil a une zone sans aucune cellule visuelle, là où le nerf optique sort de la rétine. Voici comment la faire apparaître :</p>
        </div>
      </div>

      {/* Stage */}
      <div className="relative w-full bg-white rounded-xl overflow-hidden" style={{ aspectRatio: '4 / 1' }}>
        <div className="absolute inset-0 flex items-center justify-between px-[12%]">
          {/* Cross on the left, dot on the right (for closed-left-eye) OR mirrored */}
          {isRight ? (
            <>
              <div className="text-black font-mono-spec text-[42px] sm:text-[64px] leading-none select-none">+</div>
              <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-red-600" />
            </>
          ) : (
            <>
              <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-red-600" />
              <div className="text-black font-mono-spec text-[42px] sm:text-[64px] leading-none select-none">+</div>
            </>
          )}
        </div>
      </div>

      <div className="mt-5 grid sm:grid-cols-[1fr_auto] gap-3 items-end">
        <ol className="text-white/80 text-[13px] sm:text-[14px] space-y-1.5 list-decimal list-inside">
          <li>Place ton visage à <strong className="text-white">~40 cm</strong> de l'écran.</li>
          <li>Ferme l'œil <strong className="text-white">{isRight ? 'gauche' : 'droit'}</strong>.</li>
          <li>Fixe la croix avec l'œil <strong className="text-white">{isRight ? 'droit' : 'gauche'}</strong>.</li>
          <li>Avance/recule lentement → <strong className="text-red-400">le point rouge disparaît !</strong></li>
        </ol>
        <button
          onClick={() => setSide(isRight ? 'left' : 'right')}
          data-testid="mariotte-flip"
          className="h-10 px-4 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white text-[13px] font-semibold inline-flex items-center gap-2"
        >
          <RotateCcw size={14} /> Tester l'autre œil
        </button>
      </div>
      <p className="mt-4 text-[11.5px] text-white/45 font-mono-spec">Au guidon : ta tache aveugle peut masquer <strong className="text-red-300">une moto entière à 20 m</strong>. C'est pour ça qu'on bouge toujours la tête avant une manœuvre.</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// 2. ANGLE MORT VÉHICULE — top-down SVG view with mirror cones
// ─────────────────────────────────────────────────────────────────
export function VehicleBlindSpotDemo() {
  const stageRef = useRef(null);
  const [pos, setPos] = useState({ x: 0.78, y: 0.55 }); // normalized 0..1
  const [drag, setDrag] = useState(false);

  // Stage = 4:3. Car centered, oriented front=top.
  // Coords in 0..1 normalized space.
  const CAR = { cx: 0.5, cy: 0.5, w: 0.16, h: 0.30 };

  // Blind zones = regions NOT covered by rétroviseurs (3 zones typiques).
  // Définies en polygones normalisés.
  const blindZones = [
    {
      label: 'Angle mort arrière-droit (« 3 heures »)',
      poly: [[0.58, 0.42], [0.88, 0.40], [0.95, 0.78], [0.58, 0.75]],
    },
    {
      label: 'Angle mort arrière-gauche (« 9 heures »)',
      poly: [[0.42, 0.42], [0.12, 0.40], [0.05, 0.78], [0.42, 0.75]],
    },
    {
      label: 'Zone basse avant (capot)',
      poly: [[0.40, 0.20], [0.60, 0.20], [0.62, 0.32], [0.38, 0.32]],
    },
  ];

  // Point-in-polygon
  const pip = (x, y, poly) => {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const [xi, yi] = poly[i], [xj, yj] = poly[j];
      const intersect = ((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  };
  const inZone = blindZones.find((z) => pip(pos.x, pos.y, z.poly));
  // Si dans la voiture → ignore
  const insideCar =
    Math.abs(pos.x - CAR.cx) < CAR.w / 2 &&
    Math.abs(pos.y - CAR.cy) < CAR.h / 2;

  const handleMove = (clientX, clientY) => {
    if (!stageRef.current) return;
    const r = stageRef.current.getBoundingClientRect();
    setPos({
      x: Math.min(1, Math.max(0, (clientX - r.left) / r.width)),
      y: Math.min(1, Math.max(0, (clientY - r.top) / r.height)),
    });
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7" data-testid="demo-vehicle-blindspot">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-red-600/15 border border-red-700/30 flex items-center justify-center text-red-400 shrink-0"><Car size={18} /></div>
        <div>
          <h3 className="font-serif-editorial text-white text-[20px] sm:text-[26px] leading-tight">Trouve les angles morts</h3>
          <p className="text-white/60 text-[13px] sm:text-[14px] mt-1">Déplace le motard autour de la voiture (vue du dessus). Les zones <strong className="text-red-400">rouges</strong> ne sont couvertes par <strong className="text-white">aucun rétroviseur</strong> — c'est là qu'on t'oublie.</p>
        </div>
      </div>

      <div
        ref={stageRef}
        onMouseMove={(e) => drag && handleMove(e.clientX, e.clientY)}
        onMouseUp={() => setDrag(false)}
        onMouseLeave={() => setDrag(false)}
        onTouchMove={(e) => { const t = e.touches[0]; handleMove(t.clientX, t.clientY); }}
        className="relative w-full bg-gradient-to-b from-[#222226] via-[#161618] to-[#0a0a0b] rounded-xl overflow-hidden touch-none select-none cursor-crosshair"
        style={{ aspectRatio: '4 / 3' }}
      >
        {/* SVG layer for car + zones + mirror cones */}
        <svg viewBox="0 0 400 300" className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
          {/* Road dashed center line (vertical, behind car) */}
          <line x1="200" y1="0" x2="200" y2="300" stroke="rgba(255,255,255,0.07)" strokeWidth="2" strokeDasharray="6 10" />

          {/* Mirror visibility cones (translucent green) */}
          {/* Cône rétroviseur intérieur — arrière central, étroit */}
          <polygon points="200,150 175,300 225,300" fill="rgba(74,222,128,0.10)" stroke="rgba(74,222,128,0.30)" strokeWidth="0.8" />
          {/* Cône rétro gauche — arrière-gauche partiel */}
          <polygon points="168,165 50,300 168,300" fill="rgba(74,222,128,0.08)" stroke="rgba(74,222,128,0.25)" strokeWidth="0.8" />
          {/* Cône rétro droit — arrière-droit partiel */}
          <polygon points="232,165 350,300 232,300" fill="rgba(74,222,128,0.08)" stroke="rgba(74,222,128,0.25)" strokeWidth="0.8" />
          {/* Cône vision avant (pare-brise) — large */}
          <polygon points="200,135 40,0 360,0" fill="rgba(74,222,128,0.05)" stroke="rgba(74,222,128,0.18)" strokeWidth="0.5" />

          {/* Blind zones overlays (red translucent) */}
          {blindZones.map((z, i) => (
            <polygon
              key={i}
              points={z.poly.map(([x, y]) => `${x * 400},${y * 300}`).join(' ')}
              fill="rgba(220,38,38,0.20)"
              stroke="rgba(220,38,38,0.55)"
              strokeWidth="1"
              strokeDasharray="3 3"
            />
          ))}

          {/* Top-down car body */}
          <g transform="translate(200 150)">
            {/* Shadow */}
            <ellipse cx="0" cy="2" rx="36" ry="62" fill="rgba(0,0,0,0.55)" />
            {/* Main body */}
            <rect x="-32" y="-55" width="64" height="110" rx="14" ry="14" fill="#c81f1f" stroke="#7a0f0f" strokeWidth="1.5" />
            {/* Hood (front, upper) */}
            <rect x="-26" y="-50" width="52" height="20" rx="3" fill="#a01818" />
            {/* Trunk (rear, lower) */}
            <rect x="-26" y="32" width="52" height="18" rx="3" fill="#a01818" />
            {/* Windshield (front) */}
            <path d="M -22 -28 L 22 -28 L 18 -8 L -18 -8 Z" fill="#1c2640" opacity="0.85" />
            {/* Rear window */}
            <path d="M -18 10 L 18 10 L 22 30 L -22 30 Z" fill="#1c2640" opacity="0.85" />
            {/* Roof line */}
            <line x1="-22" y1="-8" x2="22" y2="-8" stroke="rgba(255,255,255,0.05)" strokeWidth="0.5" />
            <line x1="-22" y1="10" x2="22" y2="10" stroke="rgba(255,255,255,0.05)" strokeWidth="0.5" />
            {/* Side mirrors */}
            <rect x="-38" y="-22" width="6" height="5" rx="1" fill="#7a0f0f" />
            <rect x="32" y="-22" width="6" height="5" rx="1" fill="#7a0f0f" />
            {/* Headlights (front) */}
            <circle cx="-18" cy="-50" r="3" fill="#fff8c5" stroke="#ddc100" strokeWidth="0.5" />
            <circle cx="18" cy="-50" r="3" fill="#fff8c5" stroke="#ddc100" strokeWidth="0.5" />
            {/* Tail lights (rear) */}
            <rect x="-22" y="48" width="8" height="4" rx="1" fill="#d93030" />
            <rect x="14" y="48" width="8" height="4" rx="1" fill="#d93030" />
            {/* "AVANT" label */}
            <text x="0" y="-58" textAnchor="middle" fill="rgba(255,255,255,0.55)" fontSize="8" fontFamily="monospace" letterSpacing="1.5">AVANT</text>
            <text x="0" y="62" textAnchor="middle" fill="rgba(255,255,255,0.45)" fontSize="7" fontFamily="monospace" letterSpacing="1.5">ARRIÈRE</text>
          </g>

          {/* Compass labels */}
          <text x="8" y="160" fill="rgba(255,255,255,0.3)" fontSize="8" fontFamily="monospace" letterSpacing="1.5">GAUCHE</text>
          <text x="350" y="160" fill="rgba(255,255,255,0.3)" fontSize="8" fontFamily="monospace" letterSpacing="1.5">DROITE</text>
        </svg>

        {/* Cyclist (HTML over SVG, draggable) */}
        <button
          type="button"
          data-testid="vehicle-blindspot-cyclist"
          onMouseDown={() => setDrag(true)}
          onTouchStart={() => setDrag(true)}
          className="absolute -translate-x-1/2 -translate-y-1/2 text-[28px] sm:text-[36px] cursor-grab active:cursor-grabbing select-none drop-shadow-lg"
          style={{ left: `${pos.x * 100}%`, top: `${pos.y * 100}%` }}
          aria-label="Déplacer le motard"
        >
          🏍️
        </button>

        {/* Legend overlay */}
        <div className="absolute top-2 left-2 sm:top-3 sm:left-3 flex flex-col gap-1 text-[10px] sm:text-[11px] font-mono-spec">
          <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-black/60 backdrop-blur border border-emerald-700/40 text-emerald-300">
            <span className="w-2 h-2 rounded-sm bg-emerald-500/40 border border-emerald-400/60" /> Visible par rétro
          </div>
          <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-black/60 backdrop-blur border border-red-700/40 text-red-300">
            <span className="w-2 h-2 rounded-sm bg-red-500/40 border border-red-400/60" /> Angle mort
          </div>
        </div>
      </div>

      <div className={`mt-4 rounded-xl p-3 sm:p-4 text-[13px] sm:text-[13.5px] transition-colors ${
        insideCar ? 'bg-white/[0.04] border border-white/10 text-white/60' :
        inZone ? 'bg-red-950/40 border border-red-700/40 text-red-200' :
        'bg-emerald-950/30 border border-emerald-700/30 text-emerald-300'
      }`}>
        {insideCar ? (
          <><strong>⚙ Position invalide.</strong> Tu as placé le motard sur la voiture — déplace-le sur la route.</>
        ) : inZone ? (
          <><strong>⚠ Angle mort !</strong> {inZone.label} — Aucun rétroviseur ne couvre cette zone. En moto, c'est <strong>exactement là qu'un automobiliste te « coupe »</strong> en changeant de file. Reste visible, écarte-toi, ou double franchement.</>
        ) : (
          <><strong>✓ Visible.</strong> Zone couverte par au moins un rétroviseur. Mais rappel moto : même visible, tu es <strong>une silhouette fine</strong> — un conducteur distrait peut t'ignorer. Croise toujours un regard avant de t'engager.</>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// 3. TUNNEL VISUEL SELON LA VITESSE
// ─────────────────────────────────────────────────────────────────
export function VisualTunnelDemo() {
  const [speed, setSpeed] = useState(50);
  // Angle of peripheral vision narrows from ~180° at rest → ~30° at 130 km/h
  const angle = Math.max(30, 180 - speed * 1.15);
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7" data-testid="demo-visual-tunnel">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-red-600/15 border border-red-700/30 flex items-center justify-center text-red-400 shrink-0"><Gauge size={18} /></div>
        <div>
          <h3 className="font-serif-editorial text-white text-[20px] sm:text-[26px] leading-tight">L'effet tunnel</h3>
          <p className="text-white/60 text-[13px] sm:text-[14px] mt-1">Plus tu accélères, plus ton champ visuel se rétrécit. À 130 km/h, tu ne vois quasiment que ce qui est <strong className="text-white">droit devant toi</strong>.</p>
        </div>
      </div>

      <div className="relative w-full bg-gradient-to-b from-[#1a1a1c] to-[#0a0a0b] rounded-xl overflow-hidden" style={{ aspectRatio: '16 / 9' }}>
        {/* Driver POV — fake road */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_60%,_#444_0%,_#222_30%,_#0a0a0b_70%)]" />
        {/* Trees / signs in peripheral */}
        {[0.08, 0.18, 0.82, 0.92].map((x, i) => (
          <div key={i} className="absolute text-[28px] sm:text-[36px] select-none" style={{ left: `${x * 100}%`, top: '60%', transform: 'translate(-50%, -50%)' }} aria-hidden="true">🌲</div>
        ))}
        {[0.30, 0.70].map((x, i) => (
          <div key={i} className="absolute text-[22px] sm:text-[26px] select-none" style={{ left: `${x * 100}%`, top: '50%', transform: 'translate(-50%, -50%)' }} aria-hidden="true">🚸</div>
        ))}
        {/* Black tunnel mask = peripheral vision blocked */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `radial-gradient(ellipse ${angle}% ${angle * 0.6}% at 50% 55%, transparent 0%, transparent 35%, rgba(0,0,0,0.97) 70%, #000 100%)`,
          }}
        />
        {/* Speed display */}
        <div className="absolute top-3 left-3 px-3 py-1.5 rounded-md bg-black/60 backdrop-blur border border-white/15 text-white font-mono-spec text-[13px] sm:text-[15px]">
          {speed} km/h · champ visuel : <span className="text-red-400 font-bold">{Math.round(angle)}°</span>
        </div>
      </div>

      <div className="mt-5">
        <label className="text-white/70 text-[12.5px] sm:text-[13px] font-mono-spec uppercase tracking-wider">Vitesse</label>
        <input
          data-testid="tunnel-speed-slider"
          type="range" min="30" max="130" value={speed} step="5"
          onChange={(e) => setSpeed(parseInt(e.target.value))}
          className="w-full mt-2 accent-red-500 h-2"
        />
        <div className="flex justify-between text-[11px] text-white/40 font-mono-spec mt-1">
          <span>30</span><span>50</span><span>70</span><span>90</span><span>110</span><span>130</span>
        </div>
      </div>
      <p className="mt-4 text-[11.5px] text-white/45 font-mono-spec">Source : ophtalmologie routière — perte ~1,5° par km/h au-delà de 30 km/h.</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// 4. TEST DE TEMPS DE RÉACTION
// ─────────────────────────────────────────────────────────────────
export function ReactionTimeDemo() {
  const [state, setState] = useState('idle'); // idle | waiting | ready | done | tooSoon
  const [ms, setMs] = useState(null);
  const [submittedId, setSubmittedId] = useState(null);
  const startRef = useRef(0);
  const timeoutRef = useRef(null);

  const start = () => {
    setState('waiting');
    setMs(null);
    const delay = 1200 + Math.random() * 3500;
    timeoutRef.current = setTimeout(() => {
      startRef.current = performance.now();
      setState('ready');
    }, delay);
  };

  const click = () => {
    if (state === 'waiting') {
      clearTimeout(timeoutRef.current);
      setState('tooSoon');
    } else if (state === 'ready') {
      const elapsed = Math.round(performance.now() - startRef.current);
      setMs(elapsed);
      setState('done');
    } else {
      start();
    }
  };

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  // Distance at 90 km/h = 25 m/s → distance = ms * 25 / 1000
  const distance90 = ms ? (ms * 25 / 1000).toFixed(1) : null;
  const distance50 = ms ? (ms * 13.9 / 1000).toFixed(1) : null;

  const bgColor =
    state === 'waiting' ? 'bg-red-950 hover:bg-red-900' :
    state === 'ready' ? 'bg-green-500 hover:bg-green-400' :
    state === 'tooSoon' ? 'bg-orange-700' :
    state === 'done' ? 'bg-[#141416] hover:bg-[#1a1a1c]' :
    'bg-[#141416] hover:bg-[#1a1a1c]';

  return (
    <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7" data-testid="demo-reaction-time">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-red-600/15 border border-red-700/30 flex items-center justify-center text-red-400 shrink-0"><Timer size={18} /></div>
        <div>
          <h3 className="font-serif-editorial text-white text-[20px] sm:text-[26px] leading-tight">Teste ton temps de réaction</h3>
          <p className="text-white/60 text-[13px] sm:text-[14px] mt-1">Clique dès que la zone passe au <strong className="text-green-400">VERT</strong>. Pas avant — sinon c'est un faux départ.</p>
        </div>
      </div>

      <button
        type="button"
        data-testid="reaction-zone"
        onClick={click}
        className={`relative w-full rounded-xl flex flex-col items-center justify-center select-none transition-colors ${bgColor} border border-white/10`}
        style={{ aspectRatio: '16 / 8' }}
      >
        {state === 'idle' && <><Play size={36} className="text-white/60 mb-2" /><span className="text-white/85 text-[15px] font-semibold">Clique pour démarrer</span></>}
        {state === 'waiting' && <span className="text-white text-[16px] font-semibold">Attends le VERT…</span>}
        {state === 'ready' && <span className="text-black text-[20px] sm:text-[24px] font-bold uppercase tracking-wider">CLIQUE !</span>}
        {state === 'tooSoon' && <><span className="text-white text-[16px] font-bold mb-1">Trop tôt 🚦</span><span className="text-white/80 text-[13px]">Clique pour recommencer</span></>}
        {state === 'done' && (
          <>
            <span className="font-serif-editorial text-white text-[44px] sm:text-[60px] leading-none">{ms}<span className="text-[22px] text-white/50 ml-1">ms</span></span>
            <span className="text-white/60 text-[12.5px] mt-2 font-mono-spec">Clique pour recommencer</span>
          </>
        )}
      </button>

      {state === 'done' && ms && (
        <div className="mt-5 grid sm:grid-cols-2 gap-3 text-[13px] sm:text-[14px]">
          <div className="rounded-xl bg-red-950/40 border border-red-700/30 p-4">
            <div className="text-red-300 text-[10.5px] uppercase tracking-wider font-bold mb-1">À 90 km/h</div>
            <div className="text-white font-serif-editorial text-[24px] sm:text-[30px] leading-none">{distance90} m</div>
            <div className="text-white/60 text-[12px] mt-1">parcourus avant de réagir</div>
          </div>
          <div className="rounded-xl bg-white/[0.04] border border-white/10 p-4">
            <div className="text-white/55 text-[10.5px] uppercase tracking-wider font-bold mb-1">À 50 km/h (en ville)</div>
            <div className="text-white font-serif-editorial text-[24px] sm:text-[30px] leading-none">{distance50} m</div>
            <div className="text-white/60 text-[12px] mt-1">une longueur de bus avant freinage</div>
          </div>
          <div className="sm:col-span-2 rounded-xl bg-orange-950/30 border border-orange-700/30 p-3.5">
            <div className="flex items-center gap-2 text-orange-300 text-[10.5px] uppercase tracking-wider font-bold mb-1">
              🏍️ Spécial moto · distance d'arrêt totale à 90 km/h
            </div>
            <div className="text-white/85 text-[12.5px] leading-relaxed">
              <strong className="text-white">{distance90} m de réaction + ~42 m de freinage ≈ {Math.round(parseFloat(distance90) + 42)} m d'arrêt total</strong> sur chaussée sèche. La moto freine <strong>moins fort</strong> qu'une voiture ABS (~7,5 m/s² vs 9,8). Sur chaussée mouillée, ajoute encore <strong>+40 %</strong>.
            </div>
          </div>
        </div>
      )}
      {state === 'done' && ms && (
        <>
          <SubmitScoreBlock
            slug="reaction-time"
            value={ms}
            unit="ms"
            label="Temps de réaction"
            onSubmitted={(d) => setSubmittedId(d.id)}
          />
          <Leaderboard slug="reaction-time" highlightId={submittedId} refreshKey={submittedId || 0} />
        </>
      )}
      <p className="mt-4 text-[11.5px] text-white/45 font-mono-spec">Temps de réaction moyen au guidon : <strong className="text-white/70">1,0 à 1,5 s</strong>. Casque fermé + visibilité réduite peuvent allonger la perception du danger.</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// 5. ÉNERGIE CINÉTIQUE / DISTANCE D'ARRÊT
// ─────────────────────────────────────────────────────────────────
export function KineticEnergyDemo() {
  const [speed, setSpeed] = useState(50);
  // Distance arrêt = (v/10)² + (réaction 1s)*v/3.6
  const reaction = speed / 3.6; // m in 1s
  const braking = Math.pow(speed / 10, 2);
  const total = Math.round(reaction + braking);
  // Énergie cinétique relative (50 km/h = 1.0 ref)
  const energy = Math.pow(speed / 50, 2);

  return (
    <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7" data-testid="demo-kinetic">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-red-600/15 border border-red-700/30 flex items-center justify-center text-red-400 shrink-0"><Zap size={18} /></div>
        <div>
          <h3 className="font-serif-editorial text-white text-[20px] sm:text-[26px] leading-tight">Vitesse × Énergie × Distance</h3>
          <p className="text-white/60 text-[13px] sm:text-[14px] mt-1">L'énergie d'un choc augmente avec le <strong className="text-white">carré</strong> de la vitesse. Double la vitesse → quadruple l'énergie.</p>
        </div>
      </div>

      {/* Distance bar */}
      <div className="relative w-full bg-gradient-to-r from-emerald-950/30 via-orange-950/30 to-red-950/40 rounded-xl border border-white/10 overflow-hidden h-16 sm:h-20">
        <motion.div
          className="absolute left-0 top-0 bottom-0 bg-red-600/30 border-r-2 border-red-500"
          animate={{ width: `${Math.min(100, total / 1.5)}%` }}
          transition={{ duration: 0.4 }}
        />
        <div className="absolute inset-0 flex items-center px-4">
          <span className="font-serif-editorial text-white text-[26px] sm:text-[36px] leading-none">{total}<span className="text-[14px] sm:text-[16px] text-white/60 ml-1">m d'arrêt</span></span>
        </div>
      </div>

      {/* Energy comparison */}
      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="rounded-xl bg-white/[0.04] border border-white/10 p-3 sm:p-4">
          <div className="text-white/55 text-[10.5px] uppercase tracking-wider font-mono-spec">Énergie de choc</div>
          <div className="text-white font-serif-editorial text-[22px] sm:text-[28px] leading-none mt-1">× {energy.toFixed(1)}</div>
          <div className="text-white/55 text-[11.5px] mt-1">vs choc à 50 km/h</div>
        </div>
        <div className="rounded-xl bg-white/[0.04] border border-white/10 p-3 sm:p-4">
          <div className="text-white/55 text-[10.5px] uppercase tracking-wider font-mono-spec">Équivalent chute</div>
          <div className="text-white font-serif-editorial text-[22px] sm:text-[28px] leading-none mt-1">{Math.round(Math.pow(speed / 3.6, 2) / (2 * 9.81))} m</div>
          <div className="text-white/55 text-[11.5px] mt-1">soit un étage = ~3 m</div>
        </div>
      </div>

      <div className="mt-5">
        <label className="text-white/70 text-[12.5px] sm:text-[13px] font-mono-spec uppercase tracking-wider">Vitesse : {speed} km/h</label>
        <input
          data-testid="kinetic-speed-slider"
          type="range" min="30" max="130" step="5" value={speed}
          onChange={(e) => setSpeed(parseInt(e.target.value))}
          className="w-full mt-2 accent-red-500 h-2"
        />
        <div className="flex justify-between text-[11px] text-white/40 font-mono-spec mt-1">
          <span>30</span><span>50</span><span>70</span><span>90</span><span>110</span><span>130</span>
        </div>
      </div>
      <div className="mt-4 rounded-xl bg-orange-950/25 border border-orange-700/30 p-3.5">
        <div className="flex items-center gap-2 text-orange-300 text-[10.5px] uppercase tracking-wider font-bold mb-1">
          🏍️ Spécial moto · pas de carrosserie
        </div>
        <div className="text-white/85 text-[12.5px] leading-relaxed">
          Masse moto+pilote ≈ <strong>250 kg</strong> (vs 1 400 kg voiture) → énergie plus faible à vitesse égale, MAIS <strong className="text-orange-300">aucune carrosserie n'absorbe le choc</strong> : c'est ton corps qui encaisse tout. Un choc à 50 km/h à moto ≈ une chute du 4ᵉ étage.
        </div>
      </div>
      <p className="mt-3 text-[11.5px] text-white/45 font-mono-spec">Formule officielle : distance d'arrêt = réaction (1s) + (v/10)² sur sol sec · moto majorée de ~30 %.</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// 6. DISCRIMINATION PANNEAUX — flash & identify
// ─────────────────────────────────────────────────────────────────

// French signs as inline SVG (zero network dependency)
const SIGN_LIB = {
  stop: { label: 'STOP', cat: 'Intersection',
    svg: <svg viewBox="0 0 100 100"><polygon points="30,5 70,5 95,30 95,70 70,95 30,95 5,70 5,30" fill="#d93030" stroke="#fff" strokeWidth="4"/><text x="50" y="62" textAnchor="middle" fill="#fff" fontFamily="Arial Black" fontSize="22" fontWeight="900">STOP</text></svg> },
  cedez: { label: 'Cédez le passage', cat: 'Intersection',
    svg: <svg viewBox="0 0 100 100"><polygon points="6,14 94,14 50,92" fill="#fff" stroke="#d93030" strokeWidth="9" strokeLinejoin="round"/></svg> },
  priorite: { label: 'Route prioritaire', cat: 'Priorité',
    svg: <svg viewBox="0 0 100 100"><polygon points="50,5 95,50 50,95 5,50" fill="#fff" stroke="#000" strokeWidth="3"/><polygon points="50,15 85,50 50,85 15,50" fill="#ffd417"/></svg> },
  sens_interdit: { label: 'Sens interdit', cat: 'Interdiction',
    svg: <svg viewBox="0 0 100 100"><circle cx="50" cy="50" r="45" fill="#d93030"/><rect x="20" y="42" width="60" height="16" fill="#fff"/></svg> },
  vitesse50: { label: 'Vitesse limitée 50', cat: 'Interdiction',
    svg: <svg viewBox="0 0 100 100"><circle cx="50" cy="50" r="45" fill="#fff" stroke="#d93030" strokeWidth="10"/><text x="50" y="65" textAnchor="middle" fill="#000" fontFamily="Arial" fontSize="38" fontWeight="900">50</text></svg> },
  rond_point: { label: 'Carrefour à sens giratoire', cat: 'Obligation',
    svg: <svg viewBox="0 0 100 100"><circle cx="50" cy="50" r="45" fill="#1c5fa6"/><g fill="#fff"><path d="M 50 20 A 30 30 0 0 1 76 65 L 68 60 A 22 22 0 0 0 50 28 Z"/><path d="M 76 65 A 30 30 0 0 1 24 65 L 32 60 A 22 22 0 0 0 68 60 Z"/><path d="M 24 65 A 30 30 0 0 1 50 20 L 50 28 A 22 22 0 0 0 32 60 Z"/></g><polygon points="72,70 82,68 76,78" fill="#fff"/><polygon points="28,70 24,80 18,68" fill="#fff"/><polygon points="50,15 44,25 56,25" fill="#fff"/></svg> },
  passage_pieton: { label: 'Passage piéton', cat: 'Indication',
    svg: <svg viewBox="0 0 100 100"><rect x="5" y="5" width="90" height="90" rx="4" fill="#1c5fa6" stroke="#fff" strokeWidth="3"/><polygon points="50,20 82,80 18,80" fill="#fff"/><g fill="#000"><circle cx="50" cy="42" r="4.5"/><path d="M 46 47 L 46 62 L 43 76 L 47 76 L 50 64 L 53 76 L 57 76 L 54 62 L 54 47 Z"/><path d="M 42 52 L 36 58 L 39 60 L 46 55 Z"/><path d="M 58 52 L 64 58 L 61 60 L 54 55 Z"/></g></svg> },
  travaux: { label: 'Travaux', cat: 'Danger',
    svg: <svg viewBox="0 0 100 100"><polygon points="50,8 92,86 8,86" fill="#fff" stroke="#d93030" strokeWidth="7" strokeLinejoin="round"/><g fill="#000"><circle cx="52" cy="42" r="4"/><rect x="49" y="46" width="6" height="16" rx="1"/><rect x="45" y="62" width="5" height="14" rx="1"/><rect x="55" y="62" width="5" height="14" rx="1"/><rect x="41" y="49" width="6" height="4" transform="rotate(-25 44 51)"/><rect x="55" y="47" width="6" height="4" transform="rotate(25 58 49)"/><rect x="60" y="43" width="3" height="26" transform="rotate(35 61.5 56)"/><polygon points="70,66 78,60 78,72" transform="rotate(35 74 66)"/></g></svg> },
};

const SIGN_KEYS = Object.keys(SIGN_LIB);

export function SignDiscriminationDemo() {
  const [phase, setPhase] = useState('idle'); // idle | flash | choose | result
  const [target, setTarget] = useState(null);
  const [choices, setChoices] = useState([]);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState({ ok: 0, ko: 0 });
  const [round, setRound] = useState(0);
  const totalRounds = 5;
  const flashRef = useRef(null);

  const newRound = useCallback(() => {
    const t = SIGN_KEYS[Math.floor(Math.random() * SIGN_KEYS.length)];
    const others = SIGN_KEYS.filter(k => k !== t).sort(() => Math.random() - 0.5).slice(0, 3);
    const opts = [t, ...others].sort(() => Math.random() - 0.5);
    setTarget(t);
    setChoices(opts);
    setPicked(null);
    setPhase('flash');
    flashRef.current = setTimeout(() => setPhase('choose'), 600);
  }, []);

  const pick = (key) => {
    if (phase !== 'choose') return;
    setPicked(key);
    setScore((s) => key === target ? { ...s, ok: s.ok + 1 } : { ...s, ko: s.ko + 1 });
    setPhase('result');
  };

  const next = () => {
    if (round + 1 >= totalRounds) {
      setPhase('summary');
    } else {
      setRound((r) => r + 1);
      newRound();
    }
  };

  const reset = () => {
    setRound(0); setScore({ ok: 0, ko: 0 });
    newRound();
  };

  useEffect(() => () => clearTimeout(flashRef.current), []);

  return (
    <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7" data-testid="demo-signs-discrimination">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-red-600/15 border border-red-700/30 flex items-center justify-center text-red-400 shrink-0"><Octagon size={18} /></div>
        <div className="flex-1">
          <h3 className="font-serif-editorial text-white text-[20px] sm:text-[26px] leading-tight">Discrimination de panneaux</h3>
          <p className="text-white/60 text-[13px] sm:text-[14px] mt-1">Un panneau apparaît <strong className="text-white">600 ms</strong> (comme en conduite réelle). Identifie-le parmi 4. <span className="text-white/45">5 manches.</span></p>
        </div>
        <div className="text-right text-white/60 text-[11.5px] font-mono-spec shrink-0">
          {phase !== 'idle' && phase !== 'summary' && <>Manche {round + 1} / {totalRounds}</>}
        </div>
      </div>

      {/* Stage */}
      <div className="relative w-full bg-gradient-to-b from-[#1a1a1c] to-[#0a0a0b] rounded-xl border border-white/5 flex items-center justify-center" style={{ aspectRatio: '5 / 3' }}>
        {phase === 'idle' && (
          <button onClick={() => { setRound(0); setScore({ ok: 0, ko: 0 }); newRound(); }} data-testid="signs-start" className="h-11 px-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13.5px] font-semibold inline-flex items-center gap-2">
            <Play size={14} /> Commencer
          </button>
        )}
        {phase === 'flash' && target && (
          <motion.div initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="w-28 h-28 sm:w-36 sm:h-36">
            {SIGN_LIB[target].svg}
          </motion.div>
        )}
        {phase === 'choose' && (
          <div className="text-center">
            <div className="text-white text-[14.5px] sm:text-[16px] font-semibold mb-1">Quel panneau ?</div>
            <div className="text-white/50 text-[12px]">Choisis ci-dessous ↓</div>
          </div>
        )}
        {phase === 'result' && target && (
          <div className="text-center">
            <div className={`text-[15px] sm:text-[18px] font-bold mb-2 ${picked === target ? 'text-emerald-400' : 'text-red-400'}`}>
              {picked === target ? '✓ Bonne réponse !' : '✗ Raté.'}
            </div>
            <div className="text-white/60 text-[12.5px] sm:text-[13px]">C'était : <strong className="text-white">{SIGN_LIB[target].label}</strong></div>
            <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mt-3">{SIGN_LIB[target].svg}</div>
          </div>
        )}
        {phase === 'summary' && (
          <div className="text-center px-4">
            <div className="font-serif-editorial text-white text-[28px] sm:text-[36px] leading-none">{score.ok}<span className="text-white/40 text-[18px]"> / {totalRounds}</span></div>
            <div className="text-white/60 text-[12.5px] mt-2">
              {score.ok === totalRounds && '🏆 Parfait — tu lis la route comme un pro.'}
              {score.ok >= 3 && score.ok < totalRounds && '👍 Bien. Continue à travailler les panneaux complexes.'}
              {score.ok < 3 && '⚠ À retravailler — relis le dossier ETG « Panneaux ».'}
            </div>
            <button onClick={reset} data-testid="signs-restart" className="mt-4 h-10 px-4 rounded-full border border-white/15 text-white text-[12.5px] hover:bg-white/5 inline-flex items-center gap-2"><RotateCcw size={13} /> Rejouer</button>
          </div>
        )}
      </div>

      {/* Choices */}
      {(phase === 'choose' || phase === 'result') && (
        <div className="mt-4 grid grid-cols-4 gap-2 sm:gap-3">
          {choices.map((k) => {
            const isPicked = picked === k;
            const isCorrect = k === target;
            const showResult = phase === 'result';
            return (
              <button
                key={k}
                onClick={() => pick(k)}
                data-testid={`sign-choice-${k}`}
                disabled={phase !== 'choose'}
                className={`relative aspect-square rounded-xl border p-2 sm:p-3 transition ${
                  showResult && isCorrect ? 'border-emerald-500 bg-emerald-950/30' :
                  showResult && isPicked ? 'border-red-500 bg-red-950/30' :
                  'border-white/10 bg-[#141416] hover:border-red-700/40'
                }`}
              >
                {SIGN_LIB[k].svg}
              </button>
            );
          })}
        </div>
      )}

      {phase === 'result' && (
        <button onClick={next} data-testid="signs-next" className="mt-4 w-full h-10 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13px] font-semibold">
          {round + 1 >= totalRounds ? 'Voir le résultat' : 'Manche suivante'}
        </button>
      )}

      <p className="mt-4 text-[11.5px] text-white/45 font-mono-spec">Au volant à 90 km/h, tu as ~1 s pour identifier un panneau à 25 m. C'est tout.</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// 7. SIMULATEUR FATIGUE — PVT (Psychomotor Vigilance Task)
// ─────────────────────────────────────────────────────────────────
export function FatigueDemo() {
  const TEST_DURATION = 30; // seconds
  const [phase, setPhase] = useState('idle'); // idle | running | done
  const [stim, setStim] = useState(false);
  const [reactions, setReactions] = useState([]); // ms array
  const [lapses, setLapses] = useState(0); // RT > 500ms
  const [timeLeft, setTimeLeft] = useState(TEST_DURATION);
  const [pvtSubmittedId, setPvtSubmittedId] = useState(null);
  const startRef = useRef(0);
  const stimStartRef = useRef(0);
  const tickRef = useRef(null);
  const stimRef = useRef(null);

  const scheduleStim = useCallback(() => {
    const delay = 2000 + Math.random() * 4000; // 2–6s between stimuli
    stimRef.current = setTimeout(() => {
      stimStartRef.current = performance.now();
      setStim(true);
    }, delay);
  }, []);

  const start = () => {
    setPhase('running');
    setReactions([]); setLapses(0);
    setTimeLeft(TEST_DURATION);
    startRef.current = performance.now();
    tickRef.current = setInterval(() => {
      const elapsed = (performance.now() - startRef.current) / 1000;
      const left = Math.max(0, TEST_DURATION - elapsed);
      setTimeLeft(left);
      if (left <= 0) {
        clearInterval(tickRef.current);
        clearTimeout(stimRef.current);
        setStim(false);
        setPhase('done');
      }
    }, 100);
    scheduleStim();
  };

  const onClick = () => {
    if (phase !== 'running') return;
    if (stim) {
      const rt = Math.round(performance.now() - stimStartRef.current);
      setReactions((r) => [...r, rt]);
      if (rt > 500) setLapses((l) => l + 1);
      setStim(false);
      scheduleStim();
    } else {
      // false start - penalty 1 lapse
      setLapses((l) => l + 1);
    }
  };

  useEffect(() => () => { clearInterval(tickRef.current); clearTimeout(stimRef.current); }, []);

  const avgRT = reactions.length ? Math.round(reactions.reduce((a, b) => a + b, 0) / reactions.length) : 0;
  const status = avgRT === 0 ? 'idle' : avgRT < 300 ? 'fresh' : avgRT < 450 ? 'normal' : avgRT < 600 ? 'tired' : 'critical';
  const STATUS_LABEL = {
    fresh: { c: 'text-emerald-400', t: '🟢 Frais & dispo', d: 'Réflexes au top, conduite sereine.' },
    normal: { c: 'text-yellow-400', t: '🟡 Conduite normale', d: 'Vigilance ok, fais une pause toutes les 2h.' },
    tired: { c: 'text-orange-400', t: '🟠 Fatigue installée', d: 'Équivalent à 0,5 g/L d\'alcool — fais une pause MAINTENANT.' },
    critical: { c: 'text-red-400', t: '🔴 Danger', d: 'Tu ne devrais pas être au volant. Arrête-toi.' },
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7" data-testid="demo-fatigue">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-red-600/15 border border-red-700/30 flex items-center justify-center text-red-400 shrink-0"><Coffee size={18} /></div>
        <div className="flex-1">
          <h3 className="font-serif-editorial text-white text-[20px] sm:text-[26px] leading-tight">Test de vigilance (PVT)</h3>
          <p className="text-white/60 text-[13px] sm:text-[14px] mt-1">Pendant 30 secondes, clique <strong className="text-white">dès que la zone devient rouge</strong>. Ce test est utilisé par la NASA et l'armée pour mesurer la fatigue.</p>
        </div>
        {phase === 'running' && (
          <div className="text-right text-white font-mono-spec shrink-0">
            <div className="text-[20px] sm:text-[24px] font-bold">{Math.ceil(timeLeft)}<span className="text-[12px] text-white/50">s</span></div>
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={phase === 'idle' ? start : phase === 'done' ? start : onClick}
        data-testid="fatigue-zone"
        className={`relative w-full rounded-xl flex items-center justify-center select-none transition-colors border border-white/10 ${
          stim ? 'bg-red-600' : phase === 'running' ? 'bg-[#080809]' : 'bg-[#141416] hover:bg-[#1a1a1c]'
        }`}
        style={{ aspectRatio: '16 / 7' }}
      >
        {phase === 'idle' && <><Play size={32} className="text-white/60 mr-2" /><span className="text-white text-[14.5px] font-semibold">Commencer le test (30 s)</span></>}
        {phase === 'running' && !stim && <span className="text-white/40 text-[13px] font-mono-spec">Attends le signal rouge…</span>}
        {phase === 'running' && stim && <span className="text-white text-[22px] sm:text-[28px] font-black uppercase tracking-wider">CLIQUE !</span>}
        {phase === 'done' && (
          <div className="text-center px-4 py-3">
            <div className="font-serif-editorial text-white text-[36px] sm:text-[48px] leading-none">{avgRT}<span className="text-[18px] text-white/50 ml-1">ms</span></div>
            <div className="text-white/60 text-[12px] mt-1">temps de réaction moyen · {reactions.length} stimuli · {lapses} lapses</div>
            <div className={`mt-3 text-[14px] font-semibold ${STATUS_LABEL[status]?.c || ''}`}>{STATUS_LABEL[status]?.t}</div>
            <div className="text-white/65 text-[12.5px] mt-1">{STATUS_LABEL[status]?.d}</div>
            <div className="text-white/45 text-[11.5px] mt-2">Clique pour recommencer.</div>
          </div>
        )}
      </button>

      {phase === 'done' && avgRT > 0 && (
        <>
          <SubmitScoreBlock
            slug="fatigue"
            value={avgRT}
            unit="ms"
            label="Vigilance PVT"
            onSubmitted={(d) => setPvtSubmittedId(d.id)}
          />
          <Leaderboard slug="fatigue" highlightId={pvtSubmittedId} refreshKey={pvtSubmittedId || 0} />
        </>
      )}

      <p className="mt-4 text-[11.5px] text-white/45 font-mono-spec">17 heures d'éveil = 0,5 g/L. 24 heures = 1 g/L. La fatigue tue autant que l'alcool.</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// 8. VISION FLOUE ALCOOL
// ─────────────────────────────────────────────────────────────────
export function AlcoholVisionDemo() {
  const [bac, setBac] = useState(0); // g/L

  // Effects scale with BAC
  const blur = Math.min(8, bac * 10);          // 0 → 8px
  const doubleShift = Math.min(8, bac * 11);   // 0 → 8px
  const dim = Math.max(0.55, 1 - bac * 0.4);   // 1 → 0.55
  const tunnel = Math.max(28, 60 - bac * 35);  // 60% → 28% radius

  const legalStatus =
    bac === 0 ? { c: 'emerald', t: '🟢 0,0 g/L', d: 'Conduite légale, vision normale.' } :
    bac < 0.2 ? { c: 'emerald', t: 'Sous le seuil', d: 'Conduite légale, mais déjà perte d\'acuité.' } :
    bac < 0.5 ? { c: 'yellow', t: '⚠ Seuil jeune permis (0,2 g/L)', d: 'Permis probatoire : retrait 6 points + 135 € + 3 ans suspension.' } :
    bac < 0.8 ? { c: 'orange', t: '⚠ Contravention (0,5 g/L)', d: '6 points + 135 € + suspension. Vision binoculaire altérée.' } :
    { c: 'red', t: '🚨 Délit (0,8 g/L+)', d: '8 ans de prison max, 9 000 € d\'amende, annulation du permis.' };

  return (
    <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7" data-testid="demo-alcohol-vision">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-red-600/15 border border-red-700/30 flex items-center justify-center text-red-400 shrink-0"><Wine size={18} /></div>
        <div>
          <h3 className="font-serif-editorial text-white text-[20px] sm:text-[26px] leading-tight">Ta vision sous alcool</h3>
          <p className="text-white/60 text-[13px] sm:text-[14px] mt-1">Augmente le taux d'alcool : la scène se floute, dédouble, perd en luminosité — comme ton cerveau au volant.</p>
        </div>
      </div>

      {/* Stage — simulated driving scene */}
      <div className="relative w-full rounded-xl overflow-hidden bg-black" style={{ aspectRatio: '16 / 9' }} data-testid="alcohol-scene">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'linear-gradient(180deg, #2a3552 0%, #1c2640 30%, #0a0a0b 60%, #0a0a0b 100%)',
            filter: `blur(${blur}px) brightness(${dim})`,
          }}
        />
        {/* Road perspective */}
        <div className="absolute inset-0" style={{ filter: `blur(${blur}px) brightness(${dim})` }}>
          {/* Road */}
          <div className="absolute left-1/2 bottom-0 -translate-x-1/2 w-[60%] h-[55%]" style={{ background: 'linear-gradient(to top, #2a2a2c 0%, #1a1a1c 70%, transparent 100%)', clipPath: 'polygon(20% 100%, 80% 100%, 60% 0, 40% 0)' }} />
          {/* Center line dashes */}
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="absolute left-1/2 -translate-x-1/2 bg-yellow-300/80" style={{ width: `${3 - i * 0.5}px`, height: `${8 - i}px`, bottom: `${10 + i * 12}%`, opacity: 1 - i * 0.15 }} />
          ))}
          {/* Front car taillights */}
          <div className="absolute left-1/2 -translate-x-1/2" style={{ bottom: '38%' }}>
            <div className="flex gap-3">
              <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-red-500" style={{ boxShadow: '0 0 12px rgba(239,68,68,0.9)' }} />
              <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-red-500" style={{ boxShadow: '0 0 12px rgba(239,68,68,0.9)' }} />
            </div>
          </div>
          {/* Street sign on right */}
          <div className="absolute right-[10%] top-[25%] w-12 h-12 sm:w-16 sm:h-16">
            <svg viewBox="0 0 100 100"><circle cx="50" cy="50" r="45" fill="#fff" stroke="#d93030" strokeWidth="10"/><text x="50" y="65" textAnchor="middle" fill="#000" fontFamily="Arial" fontSize="38" fontWeight="900">50</text></svg>
          </div>
        </div>
        {/* Double vision overlay */}
        {bac > 0.1 && (
          <div
            className="absolute inset-0 pointer-events-none mix-blend-screen"
            style={{
              backgroundImage: 'linear-gradient(180deg, #2a3552 0%, #1c2640 30%, #0a0a0b 60%, #0a0a0b 100%)',
              filter: `blur(${blur}px) brightness(${dim})`,
              transform: `translateX(${doubleShift}px)`,
              opacity: Math.min(0.5, bac * 0.7),
            }}
          />
        )}
        {/* Tunnel vignette */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `radial-gradient(ellipse ${tunnel}% ${tunnel * 0.7}% at 50% 50%, transparent 0%, transparent 50%, rgba(0,0,0,0.9) 100%)`,
          }}
        />
        {/* HUD */}
        <div className="absolute top-3 left-3 px-3 py-1.5 rounded-md bg-black/70 backdrop-blur border border-white/15 text-white font-mono-spec text-[12px] sm:text-[13px]">
          BAC : <span className="text-red-400 font-bold">{bac.toFixed(2)} g/L</span>
        </div>
      </div>

      {/* Slider */}
      <div className="mt-5">
        <label className="text-white/70 text-[12.5px] sm:text-[13px] font-mono-spec uppercase tracking-wider">Alcoolémie (g/L de sang)</label>
        <input
          data-testid="alcohol-slider"
          type="range" min="0" max="1.2" step="0.05" value={bac}
          onChange={(e) => setBac(parseFloat(e.target.value))}
          className="w-full mt-2 accent-red-500 h-2"
        />
        <div className="flex justify-between text-[11px] text-white/40 font-mono-spec mt-1">
          <span>0,0</span><span>0,2</span><span>0,5</span><span>0,8</span><span>1,0</span><span>1,2</span>
        </div>
      </div>

      <div className={`mt-4 rounded-xl p-3 sm:p-4 text-[13px] sm:text-[13.5px] border transition-colors ${
        legalStatus.c === 'red' ? 'bg-red-950/40 border-red-700/50 text-red-200' :
        legalStatus.c === 'orange' ? 'bg-orange-950/40 border-orange-700/50 text-orange-200' :
        legalStatus.c === 'yellow' ? 'bg-yellow-950/30 border-yellow-700/40 text-yellow-200' :
        'bg-emerald-950/30 border-emerald-700/30 text-emerald-200'
      }`}>
        <div className="font-bold mb-0.5">{legalStatus.t}</div>
        <div className="text-[12.5px] opacity-90">{legalStatus.d}</div>
      </div>

      <p className="mt-4 text-[11.5px] text-white/45 font-mono-spec">À 0,5 g/L : temps de réaction × 2, vision périphérique réduite de 30 %. Article L234-1 du Code de la route.</p>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// 9. CONTRE-BRAQUAGE — exclusivité MOTO Code 2 Roues
//    À > 20 km/h, pousser le guidon à GAUCHE fait tourner à GAUCHE.
//    Phénomène physique : effet gyroscopique des roues.
// ─────────────────────────────────────────────────────────────────

export function CounterSteeringDemo() {
  const [speed, setSpeed] = useState(70);         // km/h
  const [pushLeft, setPushLeft] = useState(0);    // -1 (push droite) à +1 (push gauche)
  const reduceMotion = useReducedMotion();

  // Physique vulgarisée (seuil officiel formation permis moto : ~30 km/h) :
  // < 30 km/h → braquage DIRECT (comme un vélo arrêté). Le guidon
  //            poussé à gauche fait tourner la roue avant à gauche
  //            → la moto part à gauche aussi.
  // > 30 km/h → effet gyroscopique dominant. Un couple au guidon
  //            incline la moto du côté opposé, l'inclinaison
  //            fait tourner. Donc pousser à gauche = tourner à gauche.
  // Sources : digischool, motoservices, fiche permis moto n°12.
  const isCounterSteering = speed >= 30;
  // Direction effective de la moto en fonction du régime
  // En contre-braquage : push gauche (+1) → tourne gauche (-angle)
  //                     push droite (-1)  → tourne droite (+angle)
  // En braquage direct : push gauche (+1) → tourne gauche (-angle) également
  //   (dans les deux cas la moto va à gauche quand on pousse à gauche,
  //    mais à basse vitesse c'est l'orientation de la roue qui compte,
  //    à haute vitesse c'est l'inclinaison provoquée par le couple)
  const motoAngle = -pushLeft * (isCounterSteering ? 30 : 10); // deg, moto inclinée
  const barAngle = pushLeft * 25;                              // deg, guidon tourné à l'inverse visuel
  const trajectoryDir = pushLeft; // +1 = gauche visuelle

  // Explication contextuelle
  let hint;
  if (Math.abs(pushLeft) < 0.1) {
    hint = { c: 'bg-white/[0.04] border-white/10 text-white/70', t: 'Appuie sur un bouton ← ou → pour pousser le guidon côté correspondant.' };
  } else if (isCounterSteering) {
    const side = pushLeft > 0 ? 'GAUCHE' : 'DROITE';
    hint = {
      c: 'bg-red-950/40 border-red-700/40 text-red-200',
      t: `À ${speed} km/h · tu pousses le guidon à ${side} → la moto s'incline et tourne à ${side}. C'est le CONTRE-BRAQUAGE, le vrai mode de pilotage moto au-delà de 30 km/h (effet gyroscopique dominant).`,
    };
  } else {
    const side = pushLeft > 0 ? 'GAUCHE' : 'DROITE';
    hint = {
      c: 'bg-emerald-950/30 border-emerald-700/40 text-emerald-200',
      t: `À ${speed} km/h · régime lent · braquage DIRECT (comme un vélo à l'arrêt). Le guidon tourne la roue avant qui emmène la moto. ${side}.`,
    };
  }

  return (
    <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7" data-testid="demo-counter-steering">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-red-600/15 border border-red-700/30 flex items-center justify-center text-red-400 shrink-0"><Compass size={18} /></div>
        <div>
          <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-red-600/15 border border-red-700/30 text-[9px] font-black uppercase tracking-[0.22em] text-red-400 mb-1.5">
            🏍️ Exclusivité moto · Code 2 Roues
          </div>
          <h3 className="font-serif-editorial text-white text-[20px] sm:text-[26px] leading-tight">Contre-braquage : pourquoi on tourne à l'envers</h3>
          <p className="text-white/60 text-[13px] sm:text-[14px] mt-1">À partir de <strong className="text-red-300">30 km/h</strong>, pousser le guidon à <strong className="text-red-300">GAUCHE</strong> fait tourner la moto à <strong className="text-red-300">GAUCHE</strong>. L'effet gyroscopique prend le dessus. Change la vitesse et observe la différence.</p>
        </div>
      </div>

      {/* Scène vue de dessus */}
      <div className="relative w-full bg-gradient-to-b from-[#1a1a1e] to-[#0a0a0b] rounded-xl overflow-hidden border border-white/5" style={{ aspectRatio: '16 / 9' }}>
        {/* Trajectoire courbe */}
        <svg viewBox="0 0 320 180" className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
          {/* Lignes de route */}
          <line x1="160" y1="0" x2="160" y2="180" stroke="rgba(255,255,255,0.06)" strokeWidth="1" strokeDasharray="4 8" />
          <line x1="80" y1="0" x2="80" y2="180" stroke="rgba(255,255,255,0.03)" strokeWidth="0.5" />
          <line x1="240" y1="0" x2="240" y2="180" stroke="rgba(255,255,255,0.03)" strokeWidth="0.5" />

          {/* Trajectoire future */}
          {Math.abs(pushLeft) > 0.1 && (
            <motion.path
              initial={false}
              animate={{
                d: `M 160 170 Q ${160 - trajectoryDir * 80} 90, ${160 - trajectoryDir * 110} 10`,
              }}
              transition={reduceMotion ? { duration: 0 } : { duration: 0.5, ease: 'easeOut' }}
              stroke="rgba(239,68,68,0.55)"
              strokeWidth="2.5"
              strokeDasharray="4 5"
              fill="none"
            />
          )}

          {/* Flèche de direction en haut */}
          {Math.abs(pushLeft) > 0.1 && (
            <motion.g
              initial={false}
              animate={{
                x: -trajectoryDir * 110,
                opacity: [0.6, 1, 0.6],
              }}
              transition={reduceMotion ? { duration: 0 } : { x: { duration: 0.5 }, opacity: { duration: 1.6, repeat: Infinity } }}
            >
              <polygon points="160,10 155,20 165,20" fill="rgba(239,68,68,0.9)" />
            </motion.g>
          )}
        </svg>

        {/* Moto vue de dessus (SVG stylisé) */}
        <motion.div
          className="absolute left-1/2 bottom-5 -translate-x-1/2"
          animate={{ rotate: motoAngle }}
          transition={reduceMotion ? { duration: 0 } : { type: 'spring', stiffness: 120, damping: 15 }}
          style={{ transformOrigin: 'center center' }}
        >
          <svg width="60" height="100" viewBox="0 0 60 100">
            {/* Ombre */}
            <ellipse cx="30" cy="92" rx="18" ry="4" fill="rgba(0,0,0,0.5)" />
            {/* Roue arrière */}
            <rect x="25" y="68" width="10" height="22" rx="3" fill="#1a1a1c" stroke="#333" strokeWidth="1" />
            {/* Corps */}
            <rect x="20" y="35" width="20" height="40" rx="6" fill="#cf2031" stroke="#7a0f0f" strokeWidth="1" />
            {/* Siège */}
            <rect x="22" y="48" width="16" height="18" rx="3" fill="#0a0a0b" />
            {/* Réservoir */}
            <ellipse cx="30" cy="42" rx="7" ry="8" fill="#e04551" />
            {/* Guidon (pivote) */}
            <motion.g
              animate={{ rotate: barAngle }}
              transition={reduceMotion ? { duration: 0 } : { type: 'spring', stiffness: 180, damping: 14 }}
              style={{ transformOrigin: '30px 25px' }}
            >
              <rect x="10" y="22" width="40" height="4" rx="2" fill="#0a0a0b" />
              <circle cx="12" cy="24" r="3" fill="#1a1a1c" />
              <circle cx="48" cy="24" r="3" fill="#1a1a1c" />
            </motion.g>
            {/* Roue avant */}
            <rect x="25" y="10" width="10" height="18" rx="3" fill="#1a1a1c" stroke="#333" strokeWidth="1" />
            {/* Phare */}
            <circle cx="30" cy="14" r="2.5" fill="#fff8c5" />
          </svg>
        </motion.div>

        {/* Badge vitesse */}
        <div className="absolute top-2 left-2 inline-flex items-center gap-1.5 px-2 py-1 rounded bg-black/70 backdrop-blur border border-white/10 text-[10px] font-mono-spec">
          <span className="text-white/60">Vitesse :</span>
          <span className="text-red-400 font-bold">{speed} km/h</span>
        </div>
        <div className={`absolute top-2 right-2 inline-flex items-center gap-1.5 px-2 py-1 rounded border text-[9px] font-black uppercase tracking-wider backdrop-blur ${
          isCounterSteering ? 'bg-red-600/20 border-red-500/40 text-red-200' : 'bg-emerald-600/20 border-emerald-500/40 text-emerald-200'
        }`}>
          {isCounterSteering ? '⚡ Contre-braquage' : '🐢 Braquage direct'}
        </div>
      </div>

      {/* Contrôles */}
      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-white/70 text-[12.5px] sm:text-[13px] font-mono-spec uppercase tracking-wider">Vitesse : {speed} km/h</label>
          <input
            data-testid="counter-steering-speed-slider"
            type="range" min="5" max="130" step="5" value={speed}
            onChange={(e) => setSpeed(parseInt(e.target.value))}
            className="w-full mt-2 accent-red-500 h-2"
          />
          <div className="flex justify-between text-[10px] text-white/40 font-mono-spec mt-1">
            <span>5</span><span className="text-emerald-400">30 (seuil)</span><span>50</span><span>90</span><span>130</span>
          </div>
        </div>

        <div>
          <div className="text-white/70 text-[12.5px] sm:text-[13px] font-mono-spec uppercase tracking-wider mb-2">Pousse le guidon</div>
          <div className="grid grid-cols-3 gap-1.5">
            <button
              type="button"
              onClick={() => setPushLeft(1)}
              data-testid="counter-steering-push-left"
              className={`h-10 rounded-lg text-[11px] font-black uppercase tracking-wider transition ${
                pushLeft > 0.5 ? 'bg-red-600 text-white' : 'bg-white/[0.04] border border-white/10 text-white/70 hover:bg-white/10'
              }`}
            >
              ← Gauche
            </button>
            <button
              type="button"
              onClick={() => setPushLeft(0)}
              data-testid="counter-steering-release"
              className={`h-10 rounded-lg text-[11px] font-black uppercase tracking-wider transition ${
                Math.abs(pushLeft) < 0.1 ? 'bg-white/15 text-white' : 'bg-white/[0.04] border border-white/10 text-white/50 hover:bg-white/10'
              }`}
            >
              Relâche
            </button>
            <button
              type="button"
              onClick={() => setPushLeft(-1)}
              data-testid="counter-steering-push-right"
              className={`h-10 rounded-lg text-[11px] font-black uppercase tracking-wider transition ${
                pushLeft < -0.5 ? 'bg-red-600 text-white' : 'bg-white/[0.04] border border-white/10 text-white/70 hover:bg-white/10'
              }`}
            >
              Droite →
            </button>
          </div>
        </div>
      </div>

      <div className={`mt-4 rounded-xl p-3 sm:p-4 text-[13px] sm:text-[13.5px] border transition-colors ${hint.c}`} data-testid="counter-steering-hint">
        {hint.t}
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        <div className="rounded-xl bg-emerald-950/20 border border-emerald-700/25 p-3">
          <div className="text-emerald-300 text-[10.5px] uppercase tracking-wider font-bold mb-1">🐢 Sous 30 km/h · braquage DIRECT</div>
          <div className="text-white/80 text-[12px] leading-relaxed">
            Comme un vélo à l'arrêt. L'effet gyroscopique est trop faible, tu tournes le guidon vers la direction souhaitée. Utile pour les manœuvres de parking, demi-tours lents, plateau.
          </div>
        </div>
        <div className="rounded-xl bg-red-950/25 border border-red-700/25 p-3">
          <div className="text-red-300 text-[10.5px] uppercase tracking-wider font-bold mb-1">⚡ Au-delà de 30 km/h · CONTRE-BRAQUAGE</div>
          <div className="text-white/80 text-[12px] leading-relaxed">
            L'effet gyroscopique domine. Pousser à gauche incline la moto à droite… non à <strong>gauche</strong> ! Le couple crée l'angle, l'angle crée le virage. C'est contre-intuitif mais c'est <strong>le seul moyen</strong> de piloter à allure soutenue.
          </div>
        </div>
      </div>

      <p className="mt-4 text-[11.5px] text-white/45 font-mono-spec">Seuil officiel formation permis moto : <strong className="text-white/70">30 km/h</strong>. Sources : fiche permis moto n°12 (motoservices, permispratique), digischool, « Motorcycle Dynamics » V. Cossalter (2006), Prévention Routière BSR/A1/A2.</p>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// 10. FREINAGE URGENCE MOTO — dosage frein avant / arrière
// ─────────────────────────────────────────────────────────────────

export function EmergencyBrakingDemo() {
  const [front, setFront] = useState(50);     // % frein avant
  const [rear, setRear] = useState(30);       // % frein arrière
  const [wet, setWet] = useState(false);      // chaussée mouillée
  const speed = 90;                           // km/h de départ (fixe)
  const reduceMotion = useReducedMotion();

  // Modélisation simplifiée (vulgarisée) :
  // - Freinage optimal moto sèche : 70 % avant / 30 % arrière, décél. 7.5 m/s²
  // - Chaussée mouillée : 60 % avant / 40 % arrière, décél. 4.5 m/s²
  // - Frein avant seul à fond (>85%) : risque de blocage/chute
  // - Frein arrière seul (>60%) sans avant : glissade latérale
  const front_n = front / 100;
  const rear_n = rear / 100;

  // Efficacité (0..1) — maximum à la répartition idéale
  const ideal_f = wet ? 0.60 : 0.70;
  const ideal_r = wet ? 0.40 : 0.30;
  const max_decel = wet ? 4.5 : 7.5;
  // Score = combinaison du freinage total et de la proximité à l'idéal
  const total_pressure = Math.min(1, front_n * ideal_f + rear_n * ideal_r);
  const efficiency = total_pressure * (1 - 0.5 * Math.abs(front_n / Math.max(0.01, front_n + rear_n) - ideal_f / (ideal_f + ideal_r)));

  const decel = Math.max(0.5, efficiency * max_decel); // m/s²
  const v_ms = speed / 3.6;
  const dist_braking = Math.round((v_ms * v_ms) / (2 * decel));
  const dist_reaction = Math.round(v_ms); // 1 s de réaction
  const dist_total = dist_reaction + dist_braking;

  // Risques :
  // - Front > 85 → risque blocage/high-side
  // - Rear > 60 et front < 20 → risque glissade (low-side)
  // - Rien activé → aucun freinage
  let risk;
  if (front >= 85 && !wet) {
    risk = { c: 'bg-red-950/40 border-red-700/50 text-red-200', t: '🚨 Danger ! Trop de frein avant → blocage de roue, chute par l\'avant (highside). Dose progressivement.', level: 'danger' };
  } else if (front >= 70 && wet) {
    risk = { c: 'bg-red-950/40 border-red-700/50 text-red-200', t: '🚨 Trop fort sur l\'avant sur sol mouillé ! Blocage probable → chute. Max 60 % sous la pluie.', level: 'danger' };
  } else if (rear >= 60 && front < 20) {
    risk = { c: 'bg-orange-950/35 border-orange-700/40 text-orange-200', t: '⚠ Frein arrière dominant → risque de glissade latérale (lowside). L\'avant doit TOUJOURS être prépondérant.', level: 'warn' };
  } else if (front + rear < 20) {
    risk = { c: 'bg-white/[0.04] border-white/10 text-white/60', t: 'Freinage insuffisant. Augmente les pressions pour arrêter la moto.', level: 'none' };
  } else if (efficiency > 0.80) {
    risk = { c: 'bg-emerald-950/30 border-emerald-700/40 text-emerald-200', t: '✓ Freinage optimal ! Tu respectes la règle 70/30 (ou 60/40 sous la pluie). Progressivité + fermeté.', level: 'ok' };
  } else {
    risk = { c: 'bg-amber-950/25 border-amber-700/35 text-amber-200', t: 'Freinage correct mais pas optimal. Vise 70 % avant / 30 % arrière sur sec.', level: 'warn' };
  }

  return (
    <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7" data-testid="demo-emergency-braking">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-red-600/15 border border-red-700/30 flex items-center justify-center text-red-400 shrink-0"><AlertTriangle size={18} /></div>
        <div>
          <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-red-600/15 border border-red-700/30 text-[9px] font-black uppercase tracking-[0.22em] text-red-400 mb-1.5">
            🏍️ Exclusivité moto · Code 2 Roues
          </div>
          <h3 className="font-serif-editorial text-white text-[20px] sm:text-[26px] leading-tight">Freinage d'urgence : dose ton pied + ta main</h3>
          <p className="text-white/60 text-[13px] sm:text-[14px] mt-1">À 90 km/h, un cerf traverse. Combien de frein <strong className="text-red-300">avant</strong> et <strong className="text-red-300">arrière</strong> pour t'arrêter le plus vite SANS chuter ?</p>
        </div>
      </div>

      {/* Visualisation distance d'arrêt */}
      <div className="relative w-full bg-gradient-to-r from-emerald-950/30 via-amber-950/20 to-red-950/40 rounded-xl border border-white/10 overflow-hidden h-20 sm:h-24">
        <motion.div
          className={`absolute left-0 top-0 bottom-0 border-r-2 ${
            risk.level === 'ok' ? 'bg-emerald-600/30 border-emerald-500' :
            risk.level === 'danger' ? 'bg-red-600/40 border-red-500' :
            'bg-amber-600/30 border-amber-500'
          }`}
          animate={{ width: `${Math.min(100, dist_total / 2.2)}%` }}
          transition={reduceMotion ? { duration: 0 } : { duration: 0.4 }}
        />
        <div className="absolute inset-0 flex items-center justify-between px-3 sm:px-5">
          <div>
            <div className="text-[10px] text-white/50 uppercase tracking-wider font-mono-spec">Depuis 90 km/h</div>
            <div className="font-serif-editorial text-white text-[26px] sm:text-[36px] leading-none">{dist_total}<span className="text-[14px] sm:text-[18px] text-white/60 ml-1">m d'arrêt</span></div>
          </div>
          <div className="text-right">
            <div className="text-[10px] text-white/50 uppercase tracking-wider font-mono-spec">Décélération</div>
            <div className="font-serif-editorial text-white text-[18px] sm:text-[24px] leading-none">{decel.toFixed(1)}<span className="text-[10px] sm:text-[12px] text-white/60 ml-1">m/s²</span></div>
          </div>
        </div>
      </div>

      {/* Sliders frein avant / arrière + toggle chaussée */}
      <div className="mt-5 grid gap-4">
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-white/70 text-[12.5px] sm:text-[13px] font-mono-spec uppercase tracking-wider">🖐 Frein avant (main droite)</label>
            <span className={`text-[13px] font-black ${front >= 85 && !wet ? 'text-red-400' : front >= 70 && wet ? 'text-red-400' : 'text-white'}`}>{front} %</span>
          </div>
          <input
            data-testid="braking-front-slider"
            type="range" min="0" max="100" step="5" value={front}
            onChange={(e) => setFront(parseInt(e.target.value))}
            className="w-full accent-red-500 h-2"
          />
          <div className="flex justify-between text-[10px] text-white/40 font-mono-spec mt-0.5">
            <span>0</span><span className={!wet ? 'text-emerald-400' : ''}>idéal sec = 70</span><span className={wet ? 'text-emerald-400' : ''}>idéal pluie = 60</span><span>100</span>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-white/70 text-[12.5px] sm:text-[13px] font-mono-spec uppercase tracking-wider">🦶 Frein arrière (pied droit)</label>
            <span className={`text-[13px] font-black ${rear >= 60 && front < 20 ? 'text-orange-400' : 'text-white'}`}>{rear} %</span>
          </div>
          <input
            data-testid="braking-rear-slider"
            type="range" min="0" max="100" step="5" value={rear}
            onChange={(e) => setRear(parseInt(e.target.value))}
            className="w-full accent-red-500 h-2"
          />
          <div className="flex justify-between text-[10px] text-white/40 font-mono-spec mt-0.5">
            <span>0</span><span className={!wet ? 'text-emerald-400' : ''}>idéal sec = 30</span><span className={wet ? 'text-emerald-400' : ''}>idéal pluie = 40</span><span>100</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setWet(false)}
            data-testid="braking-dry"
            className={`flex-1 h-10 rounded-lg text-[11.5px] font-black uppercase tracking-wider transition ${
              !wet ? 'bg-amber-500 text-black' : 'bg-white/[0.04] border border-white/10 text-white/60 hover:bg-white/10'
            }`}
          >
            ☀ Sol sec
          </button>
          <button
            type="button"
            onClick={() => setWet(true)}
            data-testid="braking-wet"
            className={`flex-1 h-10 rounded-lg text-[11.5px] font-black uppercase tracking-wider transition ${
              wet ? 'bg-blue-500 text-white' : 'bg-white/[0.04] border border-white/10 text-white/60 hover:bg-white/10'
            }`}
          >
            🌧 Sol mouillé
          </button>
        </div>
      </div>

      <div className={`mt-4 rounded-xl p-3 sm:p-4 text-[13px] sm:text-[13.5px] border transition-colors ${risk.c}`} data-testid="braking-feedback">
        {risk.t}
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        <div className="rounded-xl bg-white/[0.04] border border-white/10 p-3">
          <div className="text-white/55 text-[10.5px] uppercase tracking-wider font-mono-spec">Répartition idéale sec</div>
          <div className="text-white font-serif-editorial text-[18px] sm:text-[22px] leading-none mt-1">70 % avant · 30 % arrière</div>
          <div className="text-white/60 text-[11.5px] mt-1">Décélération ~7,5 m/s² · arrêt total ~{Math.round((v_ms * v_ms) / (2 * 7.5)) + Math.round(v_ms)} m</div>
        </div>
        <div className="rounded-xl bg-white/[0.04] border border-white/10 p-3">
          <div className="text-white/55 text-[10.5px] uppercase tracking-wider font-mono-spec">Répartition idéale pluie</div>
          <div className="text-white font-serif-editorial text-[18px] sm:text-[22px] leading-none mt-1">60 % avant · 40 % arrière</div>
          <div className="text-white/60 text-[11.5px] mt-1">Décélération ~4,5 m/s² · arrêt total ~{Math.round((v_ms * v_ms) / (2 * 4.5)) + Math.round(v_ms)} m</div>
        </div>
      </div>

      <p className="mt-4 text-[11.5px] text-white/45 font-mono-spec">Règle d'or : <strong className="text-white/70">progressivité + fermeté</strong>. On attaque toujours par l'avant (0,3 s avant l'arrière) pour transférer le poids. Sources : BSR, Prévention Routière, Moto École 2.0.</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// 11. TRAJECTOIRE DE VIRAGE — extérieur / intérieur / extérieur
// ─────────────────────────────────────────────────────────────────

export function CorneringDemo() {
  const [radius, setRadius] = useState(40);   // rayon de courbure (10 serré → 80 large)
  const [showIdeal, setShowIdeal] = useState(true);
  const [showNaive, setShowNaive] = useState(true);
  const reduceMotion = useReducedMotion();

  // Rayon effectif de la trajectoire IDÉALE est ~2× plus large que la naïve
  // (grâce au point de corde intérieur). Angle d'inclinaison nécessaire :
  // tan(θ) = v² / (g × R), v=50 km/h, g=9.81
  const v_ms = 50 / 3.6;
  const g = 9.81;
  const r_naive = radius;                 // mètres
  const r_ideal = radius * 1.8;           // trajectoire élargie
  const angle_naive = Math.min(50, Math.atan((v_ms * v_ms) / (g * r_naive)) * 180 / Math.PI);
  const angle_ideal = Math.min(50, Math.atan((v_ms * v_ms) / (g * r_ideal)) * 180 / Math.PI);

  // SVG geometry : la route = arc de cercle de rayon 100 vers la gauche
  // Trajectoire naïve = suit le milieu de la route
  // Trajectoire idéale = extérieur en entrée → corde intérieur → extérieur en sortie

  return (
    <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7" data-testid="demo-cornering">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-red-600/15 border border-red-700/30 flex items-center justify-center text-red-400 shrink-0"><Route size={18} /></div>
        <div>
          <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-red-600/15 border border-red-700/30 text-[9px] font-black uppercase tracking-[0.22em] text-red-400 mb-1.5">
            🏍️ Exclusivité moto · Code 2 Roues
          </div>
          <h3 className="font-serif-editorial text-white text-[20px] sm:text-[26px] leading-tight">Trajectoire : extérieur · intérieur · extérieur</h3>
          <p className="text-white/60 text-[13px] sm:text-[14px] mt-1">Le bon motard élargit sa courbe. Compare la trajectoire <strong className="text-emerald-300">idéale</strong> et la trajectoire <strong className="text-red-300">naïve</strong>.</p>
        </div>
      </div>

      {/* Scène SVG — virage à gauche vue du ciel */}
      <div className="relative w-full bg-gradient-to-b from-[#1a1a1e] to-[#0a0a0b] rounded-xl overflow-hidden border border-white/5" style={{ aspectRatio: '4 / 3' }}>
        <svg viewBox="0 0 320 240" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid meet">
          {/* Route — bande grise avec deux lignes blanches */}
          {/* Bord extérieur (grand rayon) */}
          <path d="M 20 240 L 20 160 A 180 180 0 0 1 200 20 L 320 20" fill="none" stroke="#2a2a2c" strokeWidth="60" />
          {/* Marquages bords */}
          <path d="M 20 240 L 20 160 A 180 180 0 0 1 200 20 L 320 20" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" />
          <path d="M 80 240 L 80 160 A 120 120 0 0 1 200 80 L 320 80" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" />
          {/* Ligne centrale pointillée */}
          <path d="M 50 240 L 50 160 A 150 150 0 0 1 200 50 L 320 50" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="1.2" strokeDasharray="5 7" />

          {/* Trajectoire NAÏVE (reste au milieu, serrée) */}
          {showNaive && (
            <>
              <path
                d="M 50 240 L 50 160 A 150 150 0 0 1 200 50 L 320 50"
                fill="none" stroke="rgba(239,68,68,0.75)" strokeWidth="2.5" strokeDasharray="6 4"
              />
              <circle cx="50" cy="230" r="4" fill="#ef4444" />
              <text x="60" y="235" fill="#fca5a5" fontSize="9" fontFamily="monospace">entrée</text>
              <circle cx="52" cy="110" r="4" fill="#ef4444" />
              <text x="60" y="115" fill="#fca5a5" fontSize="9" fontFamily="monospace">angle MAX</text>
              <circle cx="195" cy="55" r="4" fill="#ef4444" />
              <text x="200" y="52" fill="#fca5a5" fontSize="9" fontFamily="monospace">sortie</text>
            </>
          )}

          {/* Trajectoire IDÉALE (ext → int → ext, large) */}
          {showIdeal && (
            <>
              <motion.path
                initial={false}
                animate={{
                  // Entrée extérieure (droite), apex intérieur (gauche), sortie extérieure (haut-droit)
                  d: `M 20 240 L 20 170 C 20 100, 80 70, 190 20 L 320 20`,
                }}
                transition={reduceMotion ? { duration: 0 } : { duration: 0.4 }}
                fill="none" stroke="rgba(16,185,129,0.95)" strokeWidth="3"
              />
              <circle cx="20" cy="230" r="5" fill="#10b981" />
              <text x="30" y="236" fill="#6ee7b7" fontSize="10" fontFamily="monospace" fontWeight="700">1. extérieur</text>
              <circle cx="85" cy="80" r="5" fill="#10b981" />
              <text x="95" y="85" fill="#6ee7b7" fontSize="10" fontFamily="monospace" fontWeight="700">2. corde (int.)</text>
              <circle cx="305" cy="25" r="5" fill="#10b981" />
              <text x="245" y="40" fill="#6ee7b7" fontSize="10" fontFamily="monospace" fontWeight="700">3. extérieur</text>
            </>
          )}
        </svg>

        {/* Badge rayon */}
        <div className="absolute top-2 left-2 inline-flex items-center gap-1.5 px-2 py-1 rounded bg-black/70 backdrop-blur border border-white/10 text-[10px] font-mono-spec">
          <span className="text-white/60">Rayon :</span>
          <span className="text-red-400 font-bold">{radius} m</span>
        </div>
        <div className="absolute top-2 right-2 inline-flex items-center gap-1.5 px-2 py-1 rounded bg-black/70 backdrop-blur border border-white/10 text-[10px] font-mono-spec">
          <span className="text-white/60">v :</span>
          <span className="text-white font-bold">50 km/h</span>
        </div>
      </div>

      {/* Slider rayon + toggles */}
      <div className="mt-5 grid gap-4">
        <div>
          <label className="text-white/70 text-[12.5px] sm:text-[13px] font-mono-spec uppercase tracking-wider">Rayon du virage : {radius} m</label>
          <input
            data-testid="cornering-radius-slider"
            type="range" min="10" max="80" step="2" value={radius}
            onChange={(e) => setRadius(parseInt(e.target.value))}
            className="w-full mt-2 accent-red-500 h-2"
          />
          <div className="flex justify-between text-[10px] text-white/40 font-mono-spec mt-1">
            <span>10 (épingle)</span><span>30</span><span>50</span><span>80 (grand)</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setShowIdeal((s) => !s)}
            data-testid="cornering-toggle-ideal"
            className={`h-10 rounded-lg text-[11.5px] font-black uppercase tracking-wider transition ${
              showIdeal ? 'bg-emerald-600 text-white' : 'bg-white/[0.04] border border-white/10 text-white/60 hover:bg-white/10'
            }`}
          >
            ✓ Idéale
          </button>
          <button
            type="button"
            onClick={() => setShowNaive((s) => !s)}
            data-testid="cornering-toggle-naive"
            className={`h-10 rounded-lg text-[11.5px] font-black uppercase tracking-wider transition ${
              showNaive ? 'bg-red-600 text-white' : 'bg-white/[0.04] border border-white/10 text-white/60 hover:bg-white/10'
            }`}
          >
            ✗ Naïve
          </button>
        </div>
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        <div className="rounded-xl bg-emerald-950/25 border border-emerald-700/35 p-3">
          <div className="text-emerald-300 text-[10.5px] uppercase tracking-wider font-bold mb-1">✓ Trajectoire idéale</div>
          <div className="text-white/85 text-[12.5px] leading-relaxed">
            Rayon effectif : <strong className="text-white">{Math.round(r_ideal)} m</strong><br />
            Angle moto : <strong className="text-emerald-300">{angle_ideal.toFixed(1)}°</strong><br />
            <span className="text-white/60 text-[11px]">Plus de visibilité, moins d'inclinaison, plus de marge.</span>
          </div>
        </div>
        <div className="rounded-xl bg-red-950/25 border border-red-700/35 p-3">
          <div className="text-red-300 text-[10.5px] uppercase tracking-wider font-bold mb-1">✗ Trajectoire naïve</div>
          <div className="text-white/85 text-[12.5px] leading-relaxed">
            Rayon effectif : <strong className="text-white">{r_naive} m</strong><br />
            Angle moto : <strong className="text-red-300">{angle_naive.toFixed(1)}°</strong><br />
            <span className="text-white/60 text-[11px]">Serré = plus d'angle = moins de marge si imprévu.</span>
          </div>
        </div>
      </div>

      <p className="mt-4 text-[11.5px] text-white/45 font-mono-spec">
        Règle : <strong className="text-white/70">entrer tard, viser large, tourner la tête vers la sortie</strong>. En virage à gauche, se positionner à droite de la voie (dans son couloir) avant d'engager. Sources : BMC, FFM, Cossalter « Motorcycle Dynamics » (2006).
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// LEADERBOARD — classement hebdomadaire anonyme (reaction-time, fatigue)
// ─────────────────────────────────────────────────────────────────

export function Leaderboard({ slug, highlightId = null, refreshKey = 0 }) {
  const [state, setState] = useState({ loading: true, rows: [], total: 0, week: '', error: null });

  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL || '';
    setState((s) => ({ ...s, loading: true, error: null }));
    fetch(`${base}/api/demos/scores/leaderboard?slug=${encodeURIComponent(slug)}&limit=10`)
      .then((r) => r.json())
      .then((d) => setState({ loading: false, rows: d.rows || [], total: d.total || 0, week: d.week || '', error: null }))
      .catch((e) => setState({ loading: false, rows: [], total: 0, week: '', error: e.message || 'Erreur' }));
  }, [slug, refreshKey]);

  const medal = (i) => (i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : null);

  return (
    <div className="mt-5 rounded-xl bg-white/[0.03] border border-white/10 p-3 sm:p-4" data-testid={`leaderboard-${slug}`}>
      <div className="flex items-center justify-between gap-3 mb-2.5">
        <div className="flex items-center gap-2 min-w-0">
          <Trophy className="w-4 h-4 text-amber-400 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-[10.5px] font-black uppercase tracking-[0.22em] text-amber-300">Top 10 · cette semaine</div>
            <div className="text-white/50 text-[11px] font-mono-spec truncate">{state.week || '—'} · {state.total} participant{state.total > 1 ? 's' : ''}</div>
          </div>
        </div>
      </div>

      {state.loading && (
        <div className="text-white/50 text-[12px] font-mono-spec py-3 text-center">Chargement du classement…</div>
      )}
      {state.error && !state.loading && (
        <div className="text-red-400 text-[12px] py-2">Classement indisponible : {state.error}</div>
      )}
      {!state.loading && !state.error && state.rows.length === 0 && (
        <div className="text-white/50 text-[12px] py-3 text-center">
          Aucun score cette semaine. <strong className="text-white/80">Sois le premier !</strong>
        </div>
      )}
      {!state.loading && state.rows.length > 0 && (
        <ol className="space-y-1">
          {state.rows.map((r, i) => {
            const isMe = highlightId && r.id === highlightId;
            return (
              <li
                key={r.id}
                className={`flex items-center gap-2.5 sm:gap-3 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg transition ${
                  isMe
                    ? 'bg-amber-500/15 border border-amber-500/40'
                    : i < 3
                      ? 'bg-white/[0.03]'
                      : ''
                }`}
                data-testid={`leaderboard-row-${slug}-${i + 1}`}
              >
                <div className={`flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-[11px] font-black ${
                  i < 3 ? 'bg-amber-500/20 text-amber-300' : 'bg-white/5 text-white/60'
                }`}>
                  {medal(i) || `#${i + 1}`}
                </div>
                <div className="flex-1 min-w-0 flex items-center gap-1.5">
                  <span className={`font-serif-editorial text-[14px] sm:text-[16px] truncate ${isMe ? 'text-amber-200' : 'text-white'}`}>
                    {r.pseudo || 'Anonyme'}
                  </span>
                  {isMe && <span className="text-[9px] font-black uppercase tracking-widest text-amber-400">(toi)</span>}
                </div>
                <div className={`flex-shrink-0 font-mono-spec text-[12.5px] sm:text-[13.5px] font-bold ${
                  isMe ? 'text-amber-200' : 'text-white/80'
                }`}>
                  {r.value} {r.unit || 'ms'}
                </div>
              </li>
            );
          })}
        </ol>
      )}
      <p className="mt-2.5 text-[10px] text-white/35 font-mono-spec text-center">
        Classement anonyme · reset chaque lundi · aucune donnée personnelle
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// SUBMIT SCORE — enregistre le score + propose pseudo + partage
// ─────────────────────────────────────────────────────────────────

export function SubmitScoreBlock({ slug, value, unit = 'ms', label = 'Score', onSubmitted }) {
  const [pseudo, setPseudo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { rank, total, id }
  const [copied, setCopied] = useState(false);

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${base}/api/demos/scores`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug, value, unit, pseudo: pseudo.trim() || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');
      setResult(data);
      if (onSubmitted) onSubmitted(data);
    } catch (e) {
      // Afficher l'erreur mais ne pas bloquer
      setResult({ error: e.message || 'Erreur' });
    } finally {
      setSubmitting(false);
    }
  };

  const share = async () => {
    const base = typeof window !== 'undefined' ? `${window.location.origin}/demos-interactives` : 'https://code2roues.fr/demos-interactives';
    const url = `${base}?challenge=${slug}&value=${value}&unit=${unit}#${slug}`;
    const shareText = `🏍️ ${label} : ${value} ${unit} sur Code 2 Roues ! Peux-tu faire mieux ? ${url}`;
    if (navigator.share) {
      try { await navigator.share({ title: `Défi Code 2 Roues · ${label}`, text: shareText, url }); return; } catch {}
    }
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      window.prompt('Copie ce lien :', url);
    }
  };

  // État 1 : pas encore soumis → formulaire pseudo
  if (!result) {
    return (
      <div className="mt-5 rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-500/[0.08] via-orange-500/[0.04] to-transparent p-3 sm:p-4" data-testid={`submit-score-${slug}`}>
        <div className="flex items-center gap-2 mb-2">
          <Trophy className="w-4 h-4 text-amber-400" />
          <div className="text-[10.5px] font-black uppercase tracking-[0.22em] text-amber-300">Entre dans le top 10 de la semaine</div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            maxLength={20}
            value={pseudo}
            onChange={(e) => setPseudo(e.target.value)}
            placeholder="Ton pseudo (optionnel)"
            className="flex-1 h-10 px-3 rounded-lg bg-black/40 border border-white/10 text-white text-[13px] placeholder:text-white/30 focus:outline-none focus:border-amber-400"
            data-testid={`submit-pseudo-${slug}`}
          />
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            data-testid={`submit-btn-${slug}`}
            className="h-10 px-4 rounded-lg bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-black text-[12px] font-black uppercase tracking-widest transition"
          >
            {submitting ? 'Envoi…' : 'Je participe'}
          </button>
        </div>
      </div>
    );
  }

  // État 2 : soumis — afficher rang + boutons
  if (result.error) {
    return (
      <div className="mt-5 rounded-xl border border-red-700/40 bg-red-950/25 p-3 text-red-200 text-[12.5px]" data-testid={`submit-error-${slug}`}>
        Impossible d&apos;enregistrer : {result.error}
      </div>
    );
  }

  return (
    <div className="mt-5 rounded-xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/[0.08] via-emerald-500/[0.04] to-transparent p-3 sm:p-4" data-testid={`submit-success-${slug}`}>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <Trophy className="w-5 h-5 text-amber-400 flex-shrink-0" />
          <div>
            <div className="text-[10.5px] font-black uppercase tracking-[0.22em] text-amber-300">Enregistré ✓</div>
            <div className="text-white font-serif-editorial text-[15px] sm:text-[17px] leading-tight">
              Tu es <strong className="text-amber-300">#{result.rank}</strong>
              <span className="text-white/60 text-[12.5px] ml-1">sur {result.total}</span>
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={share}
          data-testid={`submitted-share-${slug}`}
          className="inline-flex items-center gap-1.5 h-10 px-3.5 rounded-full bg-gradient-to-br from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 active:scale-95 text-white text-[11.5px] font-black uppercase tracking-widest shadow-lg shadow-red-900/40 transition"
        >
          {copied ? <><Check size={14} /> <span>Lien copié !</span></> : <><Share2 size={14} /> <span>Défier un ami</span></>}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// SHARE SCORE — bouton partage universel pour défier un ami
// Génère un lien /demos-interactives?challenge=<slug>&value=<ms>#<slug>
// ─────────────────────────────────────────────────────────────────

export function ShareScoreButton({ slug, value, unit = 'ms', label = 'Mon score', testId }) {
  const [copied, setCopied] = useState(false);

  const buildUrl = () => {
    const base = typeof window !== 'undefined'
      ? `${window.location.origin}/demos-interactives`
      : 'https://code2roues.fr/demos-interactives';
    const params = new URLSearchParams({ challenge: slug, value: String(value), unit });
    return `${base}?${params.toString()}#${slug}`;
  };

  const handleShare = async () => {
    const url = buildUrl();
    const shareText = `🏍️ ${label} : ${value} ${unit} sur Code 2 Roues ! Peux-tu faire mieux ? ${url}`;

    // Native share API (mobile)
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Défi Code 2 Roues · ${label}`,
          text: `${label} : ${value} ${unit}. À toi de me battre !`,
          url,
        });
        return;
      } catch {
        // Fallback silencieux si l'utilisateur annule
      }
    }

    // Fallback : copier dans le presse-papiers
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Dernier fallback : prompt
      // eslint-disable-next-line no-alert
      window.prompt('Copie ce lien pour défier un ami :', url);
    }
  };

  return (
    <button
      type="button"
      onClick={handleShare}
      data-testid={testId || `share-${slug}`}
      className="inline-flex items-center gap-1.5 h-10 sm:h-11 px-3.5 sm:px-4 rounded-full bg-gradient-to-br from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 active:scale-95 text-white text-[11.5px] sm:text-[12.5px] font-black uppercase tracking-widest shadow-lg shadow-red-900/40 transition-all"
    >
      {copied ? (
        <><Check size={14} /> <span>Lien copié !</span></>
      ) : (
        <><Share2 size={14} /> <span>Défier un ami</span></>
      )}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────
// REGISTRY — point unique pour embed & hub
// ─────────────────────────────────────────────────────────────────
export const DEMOS = {
  mariotte: {
    component: MariotteDemo,
    title: 'Tache aveugle de Mariotte',
    short: 'La croix qui disparaît quand on ferme un œil.',
    cat: 'Vision',
    color: 'red',
    icon: Eye,
  },
  'vehicle-blindspot': {
    component: VehicleBlindSpotDemo,
    title: 'Angles morts : où disparaît le motard',
    short: 'Déplace la moto autour de la voiture (vue du dessus) et trouve les zones où aucun rétroviseur ne te voit.',
    cat: 'Vision',
    color: 'red',
    icon: Car,
  },
  'visual-tunnel': {
    component: VisualTunnelDemo,
    title: 'L\'effet tunnel selon la vitesse',
    short: 'Slider 30→130 km/h : ton champ visuel se rétrécit à mesure que tu accélères.',
    cat: 'Vitesse',
    color: 'red',
    icon: Gauge,
  },
  'reaction-time': {
    component: ReactionTimeDemo,
    title: 'Test de temps de réaction',
    short: 'Clique au signal vert. Découvre combien de mètres tu parcours à 90 km/h avant de réagir.',
    cat: 'Réaction',
    color: 'red',
    icon: Timer,
  },
  kinetic: {
    component: KineticEnergyDemo,
    title: 'Énergie cinétique × distance d\'arrêt',
    short: 'Tu doubles la vitesse ? L\'énergie quadruple. La distance d\'arrêt aussi.',
    cat: 'Vitesse',
    color: 'red',
    icon: Zap,
  },
  'signs-discrimination': {
    component: SignDiscriminationDemo,
    title: 'Discrimination de panneaux',
    short: 'Flash 600 ms : identifie le panneau parmi 4. 5 manches.',
    cat: 'Panneaux',
    color: 'red',
    icon: Octagon,
  },
  fatigue: {
    component: FatigueDemo,
    title: 'Test de vigilance (PVT)',
    short: 'Mesure ton temps de réaction sur 30 s. Détecte ta fatigue comme la NASA.',
    cat: 'Réaction',
    color: 'red',
    icon: Coffee,
  },
  'alcohol-vision': {
    component: AlcoholVisionDemo,
    title: 'Ta vision sous alcool',
    short: 'Slider de 0 à 1,2 g/L : la scène se floute, dédouble, s\'assombrit.',
    cat: 'Alcool',
    color: 'red',
    icon: Wine,
  },
  'counter-steering': {
    component: CounterSteeringDemo,
    title: 'Contre-braquage : pourquoi on tourne à l\'envers',
    short: '🏍️ Exclusif moto · pousse le guidon à gauche → la moto tourne à gauche. Pourquoi ? Démo interactive.',
    cat: 'Pilotage',
    color: 'red',
    icon: Compass,
  },
  'emergency-braking': {
    component: EmergencyBrakingDemo,
    title: 'Freinage d\'urgence : dose ton pied + ta main',
    short: '🏍️ Exclusif moto · trouve la bonne répartition 70/30 (ou 60/40 sous la pluie). Chute ou arrêt ?',
    cat: 'Pilotage',
    color: 'red',
    icon: AlertTriangle,
  },
  'cornering': {
    component: CorneringDemo,
    title: 'Trajectoire : extérieur · intérieur · extérieur',
    short: '🏍️ Exclusif moto · la bonne trajectoire élargit la courbe. Compare l\'idéale et la naïve.',
    cat: 'Pilotage',
    color: 'red',
    icon: Route,
  },
};

// Wrapper for inline article embed
export function DemoEmbed({ slug }) {
  const demo = DEMOS[slug];
  if (!demo) return null;
  const Component = demo.component;
  return (
    <div className="my-8" data-testid={`demo-embed-${slug}`}>
      <Component />
    </div>
  );
}
